import type { ReviewIntent, ReviewReference, ReviewView } from "@dante/contracts";
import { reviewPageSchema, reviewReferenceSchema, reviewViewSchema } from "@dante/contracts";

type Transport = (intent: ReviewIntent) => Promise<unknown>;
function result(response: unknown): unknown {
  if (!response || typeof response !== "object") throw new Error("Invalid review response");
  if ("error" in response) throw new Error(String(response.error));
  if (!("result" in response)) throw new Error("Invalid review response");
  return response.result;
}
export function reviewClient(transport: Transport) {
  return {
    async page(after: ReviewReference | null = null) {
      return reviewPageSchema.parse(
        result(await transport({ operation: "list_page", after, limit: 50 })),
      );
    },
    async list(): Promise<ReviewView[]> {
      return reviewViewSchema.array().parse(result(await transport({ operation: "list" })));
    },
    async open(target: ReviewReference): Promise<ReviewView> {
      return reviewViewSchema.parse(result(await transport({ operation: "open", target })));
    },
    async command(
      command: Extract<ReviewIntent, { operation: "command" }>["command"],
    ): Promise<ReviewReference> {
      return reviewReferenceSchema.parse(
        result(await transport({ operation: "command", command })),
      );
    },
  };
}

/** Records explicit activity timestamps; the backend applies the bounded effort policy. */
export function reviewActivitySession(
  target: ReviewReference,
  sessionId: string,
  startedAt: number,
) {
  const activity = new Set<number>();
  let last = startedAt;
  return {
    activity(at: number) {
      if (at < startedAt || at < last) return true;
      if (!activity.has(at) && activity.size >= 256) return false;
      activity.add(at);
      last = at;
      return true;
    },
    finish(
      endedAt: number,
      foreground: boolean,
    ): Extract<ReviewIntent, { operation: "command" }>["command"] {
      return {
        command: "effort",
        target,
        session_id: sessionId,
        started_at: startedAt,
        ended_at: Math.max(last, endedAt, startedAt),
        activity_times: [...activity],
        foreground,
      };
    },
  };
}
